import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Zap, ChevronRight, X, AlertCircle, CheckCircle } from "lucide-react";
import { progressService } from "../../../../../services/progressService.js";

/* ---------- Helper builders ---------- */
const buildItemsFromTasks = (tasks = []) => {
  const items = [];
  tasks.forEach((t) => {
    const hasSubtasks = Array.isArray(t.subtasks) && t.subtasks.length > 0;
    if (hasSubtasks) {
      (t.subtasks || []).forEach((s) => {
        if (!Number.isFinite(s.energy) || s.energy <= 0) return;
        items.push({
          id: `s-${String(s.id)}`,
          type: "subtask",
          subtaskId: s.id,
          taskId: t.id,
          name: s.name,
          energy: s.energy ?? null,
          category: s.category ?? null,
          delegationQuadrant: s.delegationQuadrant ?? null,
          delegateTo: s.delegateTo ?? s.assignedTo ?? "",
          delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
          categoryOrder: Number.isFinite(s.categoryOrder) ? s.categoryOrder : null,
        });
      });
    } else if (Number.isFinite(t.energy) && t.energy > 0) {
      items.push({
        id: `t-${String(t.id)}`,
        type: "task",
        taskId: t.id,
        name: t.name,
        energy: t.energy ?? null,
        category: t.category ?? null,
        delegationQuadrant: t.delegationQuadrant ?? null,
        delegateTo: t.delegateTo ?? t.assignedTo ?? "",
        delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
        categoryOrder: Number.isFinite(t.categoryOrder) ? t.categoryOrder : null,
      });
    }
  });
  return items;
};

const buildTasksFromItems = (originalTasks = [], items = []) => {
  const lookup = {};
  (items || []).forEach((it) => {
    lookup[it.id] = {
      category: it.category ?? null,
      delegationQuadrant: it.delegationQuadrant ?? null,
      energy: it.energy ?? null,
      name: it.name ?? undefined,
      categoryOrder: Number.isFinite(it.categoryOrder) ? it.categoryOrder : null,
    };
  });

  if (!originalTasks || originalTasks.length === 0) {
    const map = {};
    items.forEach((it) => {
      if (it.type === "task") {
        const tid = it.taskId;
        map[tid] = {
          id: tid,
          name: it.name,
          energy: it.energy ?? null,
          category: lookup[it.id]?.category ?? null,
          delegationQuadrant: lookup[it.id]?.delegationQuadrant ?? null,
          delegateTo: it.delegateTo ?? it.assignedTo ?? "",
          delegationConstraint: it.delegationConstraint ?? it.constraint ?? "",
          categoryOrder: lookup[it.id]?.categoryOrder ?? null,
          subtasks: [],
        };
      }
    });
    items.forEach((it) => {
      if (it.type === "subtask") {
        const tid = it.taskId;
        if (!map[tid]) {
          map[tid] = {
            id: tid,
            name: `Task ${tid}`,
            energy: null,
            category: null,
            delegationQuadrant: null,
            delegateTo: "",
            delegationConstraint: "",
            subtasks: [],
          };
        }
        map[tid].subtasks.push({
          id: it.subtaskId,
          name: it.name,
          energy: it.energy,
          category: lookup[it.id]?.category ?? null,
          delegationQuadrant: lookup[it.id]?.delegationQuadrant ?? null,
          delegateTo: it.delegateTo ?? it.assignedTo ?? "",
          delegationConstraint: it.delegationConstraint ?? it.constraint ?? "",
          categoryOrder: lookup[it.id]?.categoryOrder ?? null,
        });
      }
    });
    return Object.values(map);
  }

  return originalTasks.map((t) => {
    const taskItemId = `t-${String(t.id)}`;
    const taskData = lookup[taskItemId] ?? {};
    const hasTaskCategory = taskItemId in lookup && 'category' in (lookup[taskItemId] || {});
    const updatedSubs = (t.subtasks || []).map((s) => {
      const subItemId = `s-${String(s.id)}`;
      const subData = lookup[subItemId] ?? {};
      const hasSubCategory = subItemId in lookup && 'category' in (lookup[subItemId] || {});
      return {
        ...s,
        energy: (subData.energy !== undefined ? subData.energy : s.energy ?? null),
        name: subData.name ?? s.name,
        // Use explicit null from lookup if it exists, otherwise fall back to original
        category: hasSubCategory ? subData.category : (s.category ?? null),
        delegationQuadrant: subData.delegationQuadrant ?? s.delegationQuadrant ?? null,
        delegateTo: s.delegateTo ?? s.assignedTo ?? "",
        delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
        categoryOrder: subData.categoryOrder ?? s.categoryOrder ?? null,
      };
    });

    return {
      ...t,
      energy: (taskData.energy !== undefined ? taskData.energy : t.energy ?? null),
      name: taskData.name ?? t.name,
      // Use explicit null from lookup if it exists, otherwise fall back to original
      category: hasTaskCategory ? taskData.category : (t.category ?? null),
      delegationQuadrant: taskData.delegationQuadrant ?? t.delegationQuadrant ?? null,
      delegateTo: t.delegateTo ?? t.assignedTo ?? "",
      delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
      categoryOrder: taskData.categoryOrder ?? t.categoryOrder ?? null,
      subtasks: updatedSubs,
    };
  });
};

/* ---------- Constants ---------- */
const BUCKET_IDS = {
  UNASSIGNED: "unassigned",
  LEVERAGE: "leverage",
  NEUTRAL: "neutral",
  OVERHEAD: "overhead",
};

const UNASSIGNED_LIMIT = 6; // NEW: Limit for Load More functionality

const bucketLabel = {
  [BUCKET_IDS.UNASSIGNED]: "TASKS BUCKET",
  [BUCKET_IDS.LEVERAGE]: "LEVERAGE TASKS (10X RETURN)",
  [BUCKET_IDS.NEUTRAL]: "NUETRAL TASKS (1X RETURN)",
  [BUCKET_IDS.OVERHEAD]: "OVERHEAD TASKS (<1X RETURN)",
};

const bucketApproach = {
  [BUCKET_IDS.LEVERAGE]: "Do a great job. Let your inner perfectionist shine.",
  [BUCKET_IDS.NEUTRAL]: "Do a strictly good job. No better.",
  [BUCKET_IDS.OVERHEAD]: "Just get it done. Actively try to do a bad job",
};

const makeColumns = (items = []) => ({
  [BUCKET_IDS.UNASSIGNED]: { id: BUCKET_IDS.UNASSIGNED, title: bucketLabel[BUCKET_IDS.UNASSIGNED], items },
  [BUCKET_IDS.LEVERAGE]: { id: BUCKET_IDS.LEVERAGE, title: bucketLabel[BUCKET_IDS.LEVERAGE], items: [] },
  [BUCKET_IDS.NEUTRAL]: { id: BUCKET_IDS.NEUTRAL, title: bucketLabel[BUCKET_IDS.NEUTRAL], items: [] },
  [BUCKET_IDS.OVERHEAD]: { id: BUCKET_IDS.OVERHEAD, title: bucketLabel[BUCKET_IDS.OVERHEAD], items: [] },
});

const normalizeCategoryOrder = (items = []) => {
  const ordered = items.map((item, idx) => ({
    ...item,
    categoryOrder: Number.isFinite(item.categoryOrder) ? item.categoryOrder : idx + 1,
  }));
  return ordered.sort((a, b) => (a.categoryOrder || 0) - (b.categoryOrder || 0));
};

const buildColumnsFromItems = (items = []) => {
  const cols = makeColumns([]);
  items.forEach((item) => {
    if (!item.category) {
      cols[BUCKET_IDS.UNASSIGNED].items.push(item);
    } else if (item.category === BUCKET_IDS.LEVERAGE) {
      cols[BUCKET_IDS.LEVERAGE].items.push(item);
    } else if (item.category === BUCKET_IDS.NEUTRAL) {
      cols[BUCKET_IDS.NEUTRAL].items.push(item);
    } else if (item.category === BUCKET_IDS.OVERHEAD) {
      cols[BUCKET_IDS.OVERHEAD].items.push(item);
    } else {
      cols[BUCKET_IDS.UNASSIGNED].items.push(item);
    }
  });

  Object.keys(cols).forEach((key) => {
    cols[key].items = normalizeCategoryOrder(cols[key].items);
  });

  return cols;
};

/* ---------- Toast Notification Component ---------- */
const Toast = ({ message, type = "info", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000); // Auto-dismiss after 5 seconds

    return () => clearTimeout(timer);
  }, [onClose]);

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-[#2E8B87] flex-shrink-0" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />;
      default:
        return <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case "success":
        return "border-[#2E8B87]";
      case "error":
        return "border-red-600";
      case "warning":
        return "border-orange-600";
      default:
        return "border-blue-600";
    }
  };

  return (
    <div className="fixed top-6 right-6 z-50 animate-slideIn">
      <div className={`bg-white rounded-lg shadow-lg border ${getBorderColor()} px-6 py-4 flex items-center gap-3 min-w-[320px] max-w-[500px]`}>
        {getIcon()}
        <p className="text-sm font-gotham font-medium text-gray-800 flex-1">
          {message}
        </p>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ---------- Component ---------- */
const ClassifyBuckets = ({ userId, subsectionId, onPreviousStep, onNextStep, onDataChange }) => {
  const service = progressService;
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef({ userId: null, subsectionId: null, done: false });

  // Load More state
  const [showAllUnassigned, setShowAllUnassigned] = useState(false);

  // Toast state
  const [toastConfig, setToastConfig] = useState({ show: false, message: "", type: "info" });
  const [warned90, setWarned90] = useState(false);
  const [warnedIncomplete, setWarnedIncomplete] = useState(false); // NEW: Track incomplete warning

  const showToast = (message, type = "info") => {
    setToastConfig({ show: true, message, type });
  };

  const hideToast = () => {
    setToastConfig({ show: false, message: "", type: "info" });
  };

  const calculateBucketEnergy = (items) => {
    const sum = (items || []).reduce((total, item) => {
      const v = (typeof item.energy === "number") ? item.energy : (item.energy ? Number(item.energy) : 0);
      return total + (Number.isFinite(v) ? v : 0);
    }, 0);
    return sum;
  };

  const calculateTotalEnergy = () => {
    if (!columns) return 0;
    const allItems = Object.values(columns).flatMap(col => col.items || []);
    const sum = (allItems || []).reduce((total, item) => {
      const v = (typeof item.energy === "number") ? item.energy : (item.energy ? Number(item.energy) : 0);
      return total + (Number.isFinite(v) ? v : 0);
    }, 0);
    return sum;
  };

  const areAllTasksClassified = () => columns && (columns[BUCKET_IDS.UNASSIGNED]?.items?.length ?? 0) === 0;

  /* ---------- Load & sync ---------- */
  useEffect(() => {
    async function fetchData() {
      if (!userId || !subsectionId) return;

      if (loadedRef.current.done &&
          loadedRef.current.userId === userId &&
          loadedRef.current.subsectionId === subsectionId) {
        return;
      }

      try {
        setLoading(true);

        const saved = await service.getUserProgress(userId, subsectionId);
        const savedTasks =
          saved?.data?.tasks ??
          saved?.tasks ??
          (saved?.data ?? {}).progress?.tasks;
        let sourceTasks = savedTasks;

        if (!Array.isArray(sourceTasks) || sourceTasks.length === 0) {
          const userData = await service.getUserDataForSection(
            userId,
            "course_career_progression",
            "delegation",
            "delegation_widget"
          );
          sourceTasks = userData?.tasks || [];
        }

        const latestTasks = (sourceTasks || []).map((t, idx) => ({
          id: t.id ?? idx + 1,
          name: t.name ?? `Task ${idx + 1}`,
          energy: typeof t.energy === "number" ? t.energy : t.energy ? Number(t.energy) : null,
          category: t.category ?? null,
          delegationQuadrant: t.delegationQuadrant ?? null,
          delegateTo: t.delegateTo ?? t.assignedTo ?? "",
          delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
          categoryOrder: Number.isFinite(t.categoryOrder) ? t.categoryOrder : null,
          subtasks: (t.subtasks || []).map((s, sidx) => ({
            id: s.id ?? `${Date.now()}-${sidx}`,
            name: s.name ?? `Subtask ${sidx + 1}`,
            energy: typeof s.energy === "number" ? s.energy : s.energy ? Number(s.energy) : null,
            category: s.category ?? null,
            delegationQuadrant: s.delegationQuadrant ?? null,
            delegateTo: s.delegateTo ?? s.assignedTo ?? "",
            delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
            categoryOrder: Number.isFinite(s.categoryOrder) ? s.categoryOrder : null,
          })),
        }));

        const items = buildItemsFromTasks(latestTasks);
        const cols = buildColumnsFromItems(items);
        const allItems = Object.values(cols).flatMap((col) => col.items || []);
        const rebuiltTasks = buildTasksFromItems(latestTasks, allItems);

        setColumns(cols);
        setTasks(rebuiltTasks);

        loadedRef.current = { userId, subsectionId, done: true };
      } catch (err) {
        console.error("❌ ClassifyBuckets: Failed to load:", err);
        setColumns(makeColumns([]));
        setTasks([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [userId, subsectionId, service]);

  /* ---------- Save on change ---------- */
  const saveBuckets = async (cols) => {
    try {
      const normalizedCols = Object.keys(cols).reduce((acc, key) => {
        const col = cols[key];
        const normalizedItems = (col.items || []).map((item, idx) => ({
          ...item,
          category: key === BUCKET_IDS.UNASSIGNED ? null : key,
          categoryOrder: idx + 1,
        }));
        acc[key] = { ...col, items: normalizedItems };
        return acc;
      }, {});

      const allItems = Object.values(normalizedCols).flatMap(c => c.items || []);
      const updatedTasks = buildTasksFromItems(tasks, allItems);
      setColumns(normalizedCols);
      setTasks(updatedTasks);
      onDataChange?.({ tasks: updatedTasks });

      setIsSaving(true);
      const res = await service.saveProgress(userId, subsectionId, { tasks: updatedTasks }, "in_progress");
      
      try {
        const reloaded = await service.getUserProgress(userId, subsectionId);
        const reloadedTasks =
          reloaded?.data?.tasks ??
          reloaded?.tasks ??
          (reloaded?.data ?? {}).progress?.tasks;
        if (reloadedTasks) {
          const reloadedItems = buildItemsFromTasks(reloadedTasks);
          const reloadedCols = buildColumnsFromItems(reloadedItems);
          const itemsAfter = Object.values(reloadedCols).flatMap(c => c.items || []);
          const rebuiltAfter = buildTasksFromItems(reloadedTasks, itemsAfter);
          setColumns(reloadedCols);
          setTasks(rebuiltAfter);
        }
      } catch (rErr) {
        console.warn("ClassifyBuckets: re-fetch after save failed:", rErr);
      }
      setIsSaving(false);
      return res;
    } catch (err) {
      console.error("ClassifyBuckets: saveBuckets failed:", err);
      setIsSaving(false);
      throw err;
    }
  };

  /* ---------- Drag handling ---------- */
  const onDragEnd = (result) => {
    if (!columns) return;
    const { source, destination } = result;
    if (!destination) return;

    const src = source.droppableId;
    const dest = destination.droppableId;

    if (src === dest) {
      const col = columns[src];
      const newItems = Array.from(col.items);
      const [moved] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, moved);

      const newCols = { ...columns, [src]: { ...col, items: newItems } };
      saveBuckets(newCols).catch(() => {});
      return;
    }

    const sourceCol = columns[src];
    const destCol = columns[dest];
    const sourceItems = Array.from(sourceCol.items);
    const destItems = Array.from(destCol.items);
    const [movedItem] = sourceItems.splice(source.index, 1);

    const newCategory = destCol.id === BUCKET_IDS.UNASSIGNED ? null : destCol.id;
    const updatedMoved = { ...movedItem, category: newCategory };

    destItems.splice(destination.index, 0, updatedMoved);

    const newCols = {
      ...columns,
      [src]: { ...sourceCol, items: sourceItems },
      [dest]: { ...destCol, items: destItems },
    };

    saveBuckets(newCols).catch(() => {});
  };

  /* ---------- UI helpers ---------- */
  const Pill = ({ item, provided, snapshot }) => {
    const child = (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`inline-flex items-center justify-between gap-3 px-4 py-2 rounded-[9.82px] bg-[#F2F2F2] border border-[#E6E6E6] shadow-sm mr-3 mb-3`}
        style={{
          minWidth: 300,
          maxWidth: 400,
          ...provided.draggableProps.style,
        }}
      >
        <div className="flex items-center gap-1">
          <div className="flex flex-col gap-[2px] opacity-100">
            <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
            <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
            <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
          </div>
          <div className="flex flex-col gap-[2px] opacity-100">
            <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
            <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
            <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
          </div>
          <div className="text-[15.71px] font-gotham font-medium text-[#000000] mx-3">{item.name}</div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-xs bg-white border border-[#E2F2F1] rounded-full px-3 py-2 flex items-center gap-2">
            <span className="text-[12px] text-[#000000] font-medium font-gotham">{item.energy ?? "-"}%</span>
            <Zap className="w-4 h-4 text-[#54B0AF]" stroke="none" fill="#54B0AF" />
          </div>
        </div>
      </div>
    );

    // Use portal when dragging to escape transformed parent
    if (snapshot?.isDragging) {
      return ReactDOM.createPortal(child, document.body);
    }

    return child;
  };

  const CategoryCard = ({ col }) => {
    const headerBg =
      col.id === BUCKET_IDS.LEVERAGE ? "bg-[#1B7977]" :
      col.id === BUCKET_IDS.NEUTRAL ? "bg-[#5AA7A0]" :
      "bg-[#87BEB8]";

    const centerLetter =
      col.id === BUCKET_IDS.LEVERAGE ? "L" :
      col.id === BUCKET_IDS.NEUTRAL ? "N" : "O";

    const centerReturns =
      col.id === BUCKET_IDS.LEVERAGE ? "10x" :
      col.id === BUCKET_IDS.NEUTRAL ? "1x" : "<1x";

    const bucketSum = calculateBucketEnergy(col.items);
    const bucketEnergyPercent = Math.round(bucketSum);
    const hasItems = col.items.length > 0;

    return (
      <div className="flex-1 flex flex-col rounded-[10px] overflow-hidden shadow-sm border border-transparent">
        <div className={`${headerBg} text-white text-center py-5 px-4 min-h-[105px]`}>
          <div className="text-[14px] 2xl:text-base font-gotham uppercase font-bold tracking-wider">{col.title}</div>
          <div className="text-[12px] 2xl:text-[14px] font-gotham font-bold mt-1 2xl:mt-2 max-w-[280px] mx-auto">
            Approach: <span className="font-normal opacity-80">{bucketApproach[col.id]}</span>
          </div>
        </div>

        <Droppable droppableId={col.id}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="flex-1 flex flex-col p-6 bg-[#E8F6F4] min-h-[300px] 2xl:min-h-[320px]"
            >
              {!hasItems && (
                <div className="flex-1 flex flex-col items-center justify-center pointer-events-none select-none gap-4">
                  <div className="text-[60px] 2xl:text-[85.22px] font-extrabold text-[rgba(46,139,135,0.15)] leading-none">
                    {centerLetter}
                  </div>
                  <svg width="28" height="40" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2v28" stroke="rgba(46,139,135,0.20)" strokeWidth="2" strokeLinecap="round" />
                    <path d="M7 24l5 6 5-6" stroke="rgba(46,139,135,0.20)" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <div className="text-[50px] 2xl:text-[64px] font-gotham font-medium text-[rgba(46,139,135,0.25)] leading-none">{centerReturns}</div>
                  <div className="text-[13px] 2xl:text-[16px] tracking-wide font-medium text-center font-gotham text-[rgba(46,139,135,0.35)] uppercase">RETURNS</div>
                </div>
              )}

              {hasItems && (
                <>
                  {/* Scrollable container for task items - max height for ~3 items */}
                  {/* Scrollable container for task items - max height for ~3 items */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden max-h-[200px] custom-scrollbar">
                    <div className="relative z-10">
                      {col.items.map((it, idx) => (
                        <Draggable key={it.id} draggableId={String(it.id)} index={idx}>
                          {(prov, snap) => (
                            <div className="inline-block w-full">
                              <Pill item={it} provided={prov} snapshot={snap} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>

                  {/* Energy Spend - fixed at bottom */}
                  <div className="flex flex-col items-center justify-center pt-4 pointer-events-none select-none">
                    <div className="text-[50px] 2xl:text-[64px] font-gotham font-medium text-[rgba(46,139,135,0.25)] leading-none">{bucketEnergyPercent}%</div>
                    <div className="text-[13px] 2xl:text-[16px] tracking-wide font-medium text-center font-gotham text-[rgba(46,139,135,0.35)] uppercase">ENERGY SPEND</div>
                  </div>
                </>
              )}

              {!hasItems && provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  /* ---------- Next button logic ---------- */
  const handleNextClick = async () => {
    const totalEnergyRounded = Math.round(calculateTotalEnergy());
    const allClassified = areAllTasksClassified();

    // Case A: 100% and all classified => proceed
    if (totalEnergyRounded === 100 && allClassified) {
      setIsSaving(true);
      try {
        const allItems = Object.values(columns).flatMap((c) => c.items || []);
        const payloadTasks = buildTasksFromItems(tasks, allItems);
        await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "completed");
        setTasks(payloadTasks);
        onNextStep?.();
      } catch (err) {
        console.error("Finish failed:", err);
        showToast("Failed to save progress. Please try again.", "error");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Case B: 90% <= total < 100 => show toast first time, require second click
    if (totalEnergyRounded >= 90 && totalEnergyRounded < 100) {
      if (!warned90) {
        showToast(`You're at ${totalEnergyRounded}% energy — almost there. Click Next again to proceed.`, "warning");
        setWarned90(true);
        return;
      }
      
      setIsSaving(true);
      try {
        const allItems = Object.values(columns).flatMap((c) => c.items || []);
        const payloadTasks = buildTasksFromItems(tasks, allItems);
        await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "completed");
        setTasks(payloadTasks);
        onNextStep?.();
      } catch (err) {
        console.error("Finish failed:", err);
        showToast("Failed to save progress. Please try again.", "error");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Case C: total < 90 OR tasks not classified => show warning toast, require second click
    if (!warnedIncomplete) {
      const messages = [];
      if (totalEnergyRounded !== 100) messages.push(`Total energy is ${totalEnergyRounded}%.`);
      if (!allClassified) messages.push("Some tasks are still in the TASKS BUCKET.");
      const warningMsg = `${messages.join(" ")} Click Next again to proceed anyway.`;
      showToast(warningMsg, "warning");
      setWarnedIncomplete(true);
      return;
    }

    // User clicked again after warning - proceed
    setIsSaving(true);
    try {
      const allItems = Object.values(columns).flatMap((c) => c.items || []);
      const payloadTasks = buildTasksFromItems(tasks, allItems);
      await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "completed");
      setTasks(payloadTasks);
      onNextStep?.();
    } catch (err) {
      console.error("Finish failed:", err);
      showToast("Failed to save progress. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        Loading bucket data...
      </div>
    );
  }

  if (!columns) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        Initializing buckets...
      </div>
    );
  }

  const unassignedItems = columns[BUCKET_IDS.UNASSIGNED].items;
  const totalUnassignedCount = unassignedItems.length;
  const hasMoreThanLimit = totalUnassignedCount > UNASSIGNED_LIMIT;
  const shouldShowLoadMoreButton = hasMoreThanLimit && !showAllUnassigned;
  const visibleUnassignedItems = shouldShowLoadMoreButton 
    ? unassignedItems.slice(0, UNASSIGNED_LIMIT) 
    : unassignedItems;

  return (
    <div className="min-h-auto bg-white">
      {/* Toast Notification */}
      {toastConfig.show && (
        <Toast
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={hideToast}
        />
      )}

      <div className="max-w-[1200px] mx-auto py-1">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="text-[14px] 2xl:text-[16px] font-medium text-[#6D6D6D] font-gotham mb-4">{bucketLabel[BUCKET_IDS.UNASSIGNED]}</div>
          <div className="rounded-[14px] border border-[#B8B8B8] px-5 py-3 mb-2 bg-white shadow-sm">
            <Droppable droppableId={BUCKET_IDS.UNASSIGNED} direction="horizontal">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`p-4 rounded-[9.82px] border-[#F3F3F3] ${snapshot.isDraggingOver ? "bg-[#E9E9E9]" : "bg-white"}`}
                  style={{ minHeight: 100 }}
                >
                  <div className="flex flex-wrap items-start">
                    {visibleUnassignedItems.map((it, idx) => (
                      <Draggable key={it.id} draggableId={String(it.id)} index={idx}>
                        {(prov, snap) => <Pill item={it} provided={prov} snapshot={snap} />}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>

                  {totalUnassignedCount === 0 && (
                    <div className="text-[15.71px] text-[#A3A3A3] font-gotham font-medium text-center py-4">
                      All tasks classified!
                    </div>
                  )}

                  {shouldShowLoadMoreButton && (
                    <div className="text-start">
                      <button
                        onClick={() => setShowAllUnassigned(true)}
                        className="text-[15.71px] text-[#A3A3A3] font-gotham font-medium hover:text-[#54B0AF] transition-colors"
                      >
                        + Load More Tasks
                      </button>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </div>

          {!shouldShowLoadMoreButton && totalUnassignedCount > 0 && (
            <div className="mb-8"></div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <CategoryCard col={columns[BUCKET_IDS.LEVERAGE]} />
            <CategoryCard col={columns[BUCKET_IDS.NEUTRAL]} />
            <CategoryCard col={columns[BUCKET_IDS.OVERHEAD]} />
          </div>
        </DragDropContext>

        <div className="flex justify-between items-center">
          <button onClick={onPreviousStep} className="flex font-gotham font-medium items-center gap-3 px-6 py-3 rounded-full bg-[#000000] text-white hover:bg-[#858585] transition-colors">
            <span className="bg-white rounded-full p-2">
              <ChevronRight className="w-4 h-4 rotate-180 text-black" />
            </span>
            Previous Step
          </button>

          <div className="flex items-center gap-4">
            <button
              onClick={handleNextClick}
              disabled={isSaving}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-gotham font-medium transition-colors ${!isSaving ? "bg-black text-white hover:bg-gray-800" : "bg-[#BDBDBD] text-white cursor-not-allowed"}`}
            >
              Next Step
              <span className="bg-white rounded-full p-2">
                <ChevronRight className="w-4 h-4 text-black" />
              </span>
            </button>
          </div>
        </div>

        {!areAllTasksClassified() && (
          <div className="mt-4 text-center text-sm text-[#666]">Please classify all tasks to proceed (you can still continue if you choose).</div>
        )}
        {areAllTasksClassified() && Math.round(calculateTotalEnergy()) !== 100 && (
          <div className="mt-4 text-center text-sm text-[#666]">Total energy is not 100% (Currently: {Math.round(calculateTotalEnergy())}%) — you may still proceed.</div>
        )}
        {Math.round(calculateTotalEnergy()) === 100 && areAllTasksClassified() && (
          <div className="mt-4 text-center text-sm text-[#2E8B87] font-medium font-gotham">✓ Ready to proceed to next step!</div>
        )}
      </div>

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }

        /* Sleek scrollbar styles */
        .custom-scrollbar::-webkit-scrollbar {
          width: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.1);
          border-radius: 20px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </div>
  );
};

export default ClassifyBuckets;